import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRightLeft } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { num } from "@/lib/formatters";

const FIELDS = [
  ["utpendling_pct", "Pendlar ut (% av sysselsette busette)", "#D35E4E"],
  ["innpendling_pct", "Pendlar inn (% av arbeidsplassane)", "#3F8A86"],
  ["arbeidsplassdekning_pct", "Arbeidsplassdekning (arbeidsplassar per 100 sysselsette)", "#103766"],
];

const PendlingCompareChart = ({ pendlingData }) => {
  const rows = Object.values(pendlingData || {}).filter((k) => k.year);
  if (rows.length === 0) return null;
  const data = rows.map((k) => ({ name: k.name, ...Object.fromEntries(FIELDS.map(([key, label]) => [label, k[key] ?? 0])) }));
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-pendling-chart">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 text-[#103766]" />
          Pendling og arbeidsplassdekning {rows[0].year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Arbeidsplassdekning over 100 tyder fleire arbeidsplassar enn sysselsette innbyggjarar.</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${num(v)} %`} />
              <Tooltip formatter={(v) => `${num(v)} %`} contentStyle={tooltipStyle} />
              <Legend itemSorter={null} />
              <ReferenceLine y={100} stroke="#8AA4BF" strokeDasharray="4 4" />
              {FIELDS.map(([key, label, color]) => <Bar key={key} dataKey={label} fill={color} radius={[4, 4, 0, 0]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="compare-pendling-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.1em] text-[#8AA4BF] border-b border-[#E6E9F0]">
                <th className="py-2 pr-3 font-semibold">Kommune</th>
                <th className="py-2 px-3 font-semibold text-right">Arbeidsplassar</th>
                <th className="py-2 px-3 font-semibold text-right">Sysselsette busette</th>
                <th className="py-2 px-3 font-semibold text-right">Utpendlarar</th>
                <th className="py-2 px-3 font-semibold text-right">Innpendlarar</th>
                <th className="py-2 pl-3 font-semibold text-right">Netto pendling</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((k) => (
                <tr key={k.name} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`compare-pendling-row-${k.name}`}>
                  <td className="py-2 pr-3 font-medium">{k.name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmt(k.arbeidsplasser)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmt(k.sysselsatte_bosatte)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmt(k.utpendlere)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmt(k.innpendlere)}</td>
                  <td className={`py-2 pl-3 text-right tabular-nums font-medium ${k.netto >= 0 ? "text-[#2F7D5B]" : "text-[#C0392B]"}`}>{k.netto > 0 ? "+" : ""}{fmt(k.netto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#8AA4BF] text-center">Kjelde: SSB 03321 – pendlingsstraumar, sysselsette 15–74 år, 4. kvartal.</p>
      </CardContent>
    </Card>
  );
};

export default PendlingCompareChart;
